import { Controller, useFormContext } from 'react-hook-form';

import { Box, Checkbox, forwardRef, Radio, SimpleGrid } from '@bonusly/design-system';

import { type UserGroup } from '../../queries/groups';

type CheckboxRuleSetProps = {
	children: string;
	disabledValues?: string[];
	isChecked: boolean;
	isDisabled?: boolean;
	name: string;
	options: UserGroup[];
};

export const CheckboxRuleSet = forwardRef<CheckboxRuleSetProps, any>(({ children, disabledValues = [], name, options, ...rest }, ref) => {
	const { control } = useFormContext();

	if (!options.length) {
		return null;
	}

	// We want to pass the ref to the first enabled checkbox. This way if the user tries to submit the form without selecting
	// a required option we'll automatically focus on it.
	const firstEnabledOption = options.find((option) => !disabledValues.includes(option.key));

	return (
		<>
			<Radio id={name} {...rest}>
				{children}
			</Radio>

			{rest.isChecked && (
				<Controller
					control={control}
					name={name}
					render={({ field: { onChange, value } }) => {
						return (
							<Box pl={8}>
								<SimpleGrid columns={2} spacing={2}>
									{options.map((option) => {
										return (
											<Checkbox
												key={option.key}
												id={`${name}:${value}`}
												isChecked={value.includes(option.key)}
												isDisabled={disabledValues.includes(option.key)}
												onChange={(e) => {
													const isChecked = e.target.checked;
													if (isChecked) {
														onChange([...value, option.key]);
													} else {
														onChange(value.filter((v: any) => v !== option.key));
													}
												}}
												ref={firstEnabledOption && firstEnabledOption.key === option.key ? ref : null}
												value={option.key}
												sx={{
													'span:last-of-type': {
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													},
												}}
											>
												{option.name}
											</Checkbox>
										);
									})}
								</SimpleGrid>
							</Box>
						);
					}}
				/>
			)}
		</>
	);
});
